import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { DataTable, DataTableVariant, Modal, SearchInput } from "@swivl-mono/ui";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

import { Member } from "../../../Models/Member/Member.Current";

const PAGE_SIZE = 10;

export interface OutboundNumberSelection {
  identifier: string;
  name: string;
}

interface NumberRow {
  id: string;
  name: string;
  isOrg: boolean;
  address: string;
  identifier: string;
}

interface SelectOutboundNumberModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (selection: OutboundNumberSelection) => void;
  selectedIdentifier?: string | null;
}

/**
 * "Select an Outbound Number" (ENG-3018, Figma 1273-17105): the org's default
 * number (Org badge), every location carrying its own number, then every other
 * active number on the org under its Twilio app name (ENG-3490 — all the org's
 * numbers, not only the assigned ones). Search and pagination are client-side —
 * one row per number, hundreds at most.
 */
export function SelectOutboundNumberModal({ open, onClose, onSelect, selectedIdentifier }: SelectOutboundNumberModalProps): React.ReactNode {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<NumberRow[]>([]);

  useEffect(() => {
    if (!open || !Member.orgId) return;
    let cancelled = false;
    setLoading(true);
    const load = async (): Promise<void> => {
      try {
        const result = await API.ContactCenterV3.getOutboundNumbers(Member.orgId!);
        if (cancelled) return;
        const orgRows: NumberRow[] = result.org
          ? [{ id: "org", name: result.org.name, isOrg: true, address: "–", identifier: result.org.identifier }]
          : [];
        const locationRows: NumberRow[] = result.locations.map((l) => ({
          id: `loc-${l.locationId}`,
          name: l.locationName,
          isOrg: false,
          address: l.address || "–",
          identifier: l.identifier
        }));
        const unassignedRows: NumberRow[] = (result.unassigned ?? []).map((n) => ({
          id: `num-${n.identifier}`,
          name: n.name,
          isOrg: false,
          address: "–",
          identifier: n.identifier
        }));
        setRows([...orgRows, ...locationRows, ...unassignedRows]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(term) || r.identifier.includes(term.replace(/[^\d+]/g, "") || term));
  }, [rows, search]);

  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const rowById = useMemo(() => new Map(pageRows.map((r) => [r.id, r])), [pageRows]);

  const columns: ColumnDef<NumberRow, string>[] = useMemo(
    () => [
      {
        header: "",
        id: "radio",
        cell: (info) => {
          const isSelected = info.row.original.identifier === selectedIdentifier;
          return (
            <span
              className={filterClasses(
                "inline-block size-4 rounded-full border-2",
                isSelected ? "border-orange-500 bg-orange-500 shadow-[inset_0_0_0_2.5px_white]" : "border-grey-300 bg-white"
              )}
            />
          );
        },
        enableSorting: false
      },
      {
        header: "Location",
        accessorKey: "name",
        cell: (info) => (
          <span className="flex items-center gap-2">
            {info.getValue()}
            {info.row.original.isOrg && <span className="rounded bg-grey-100 px-1.5 py-0.5 text-[10px] font-semibold text-grey-600">Org</span>}
          </span>
        ),
        enableSorting: false
      },
      { header: "Address", accessorKey: "address", cell: (info) => info.getValue(), enableSorting: false },
      { header: "Phone", accessorKey: "identifier", cell: (info) => info.getValue(), enableSorting: false }
    ],
    [selectedIdentifier]
  );

  const handleSelectCell = (selectedId?: string): void => {
    if (selectedId == null) return;
    const row = rowById.get(selectedId);
    if (row) {
      onSelect({ identifier: row.identifier, name: row.name });
      onClose();
    }
  };

  return (
    <Modal isOpen={open} onClose={(isOpen) => !isOpen && onClose()} title="Select an Outbound Number" className="flex h-[640px] w-[800px] flex-col">
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
        <div className="shrink-0 pb-4">
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by location or phone number" autoFocus />
        </div>
        <div className="min-h-0 flex-1">
          <DataTable
            columns={columns}
            data={pageRows}
            loading={loading}
            areRowsSelectable
            handleSelectCell={handleSelectCell}
            hasFooter
            pinFooter
            variant={DataTableVariant.SECONDARY}
            manualConfig={{ manualPagination: true, useCursorPagination: true }}
            paginationState={{
              hasNext: (page + 1) * PAGE_SIZE < filtered.length,
              hasPrev: page > 0,
              onNext: () => setPage((p) => p + 1),
              onPrev: () => setPage((p) => Math.max(0, p - 1))
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
